import { useMemo, useState } from "react";
import { CHAR_FIELDS, CHARACTER_CATEGORIES } from "./helpers";
import AutoTextarea from "./AutoTextarea";
import CharacterBriefingModal from "@/components/CharacterBriefingModal";
import CharacterRefineModal from "@/components/CharacterRefineModal";
import DetectCharactersModal from "@/components/DetectCharactersModal";
import SuggestFillModal from "@/components/SuggestFillModal";

/** Right-side panel: Characters matrix CRUD. */
export default function CharactersPanel({ characters, onAdd, onUpdate, onDelete, projectId, onRefresh }) {
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("secundario");
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [detectOpen, setDetectOpen] = useState(false);
  const [suggestForId, setSuggestForId] = useState(null);  // id del personaje sobre el que correr "Sugerir"

  // Cuenta campos vacíos de una ficha (para mostrar el botón "✨ Sugerir
  // contenido" solo en fichas poco desarrolladas: ≥4 vacíos de los 10 campos).
  const emptyFieldCount = (c) => CHAR_FIELDS.filter((f) => !(c?.[f.id] || "").trim()).length;

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    await onAdd({ name, category: newCategory });
    setNewName("");
    setNewCategory("secundario");
    setCreating(false);
  };

  // Agrupar por categoría respetando el orden de CHARACTER_CATEGORIES. Los
  // personajes sin category quedan en "secundario" (default del backend).
  const grouped = useMemo(() => {
    const map = Object.fromEntries(CHARACTER_CATEGORIES.map((c) => [c.id, []]));
    (characters || []).forEach((c) => {
      const cat = CHARACTER_CATEGORIES.find((x) => x.id === c.category) ? c.category : "secundario";
      map[cat].push(c);
    });
    return map;
  }, [characters]);

  return (
    <div data-testid="sp-characters-panel">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="font-serif text-base text-stone-900">Matriz de personajes</h4>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setDetectOpen(true)}
            data-testid="sp-character-detect-btn"
            title="Leer el guion y crear los personajes que faltan en la matriz"
            className="rounded-full border border-[#26775E]/40 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#26775E] transition hover:bg-[#26775E] hover:text-white"
          >
            📚 Leer guion
          </button>
          <button
            onClick={() => setRefineOpen(true)}
            data-testid="sp-character-refine-btn"
            title="Detecta variantes y erratas de nombres en TODO el guion"
            className="rounded-full border border-[#941620]/40 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#941620] transition hover:bg-[#941620] hover:text-white"
          >
            🪄 Refinar
          </button>
          <button
            onClick={() => setBriefingOpen(true)}
            data-testid="sp-character-briefing-btn"
            title="Crear personaje(s) desde un briefing libre con IA"
            className="rounded-full bg-[#941620] px-3 py-1 text-[10px] uppercase tracking-widest text-white hover:bg-stone-900"
          >
            ✨ Briefing
          </button>
          <button
            onClick={() => setCreating(true)}
            data-testid="sp-character-add-btn"
            className="rounded-full bg-[#26775E] px-3 py-1 text-[10px] uppercase tracking-widest text-white hover:bg-stone-900"
          >
            + Personaje
          </button>
        </div>
      </div>

      {detectOpen && (
        <DetectCharactersModal
          projectId={projectId}
          open={detectOpen}
          onClose={() => setDetectOpen(false)}
          onApplied={() => { onRefresh?.(); }}
        />
      )}
      {refineOpen && (
        <CharacterRefineModal
          projectId={projectId}
          open={refineOpen}
          onClose={() => setRefineOpen(false)}
          onApplied={() => { onRefresh?.(); }}
        />
      )}
      {suggestForId && (
        <SuggestFillModal
          projectId={projectId}
          open={!!suggestForId}
          character={(characters || []).find((c) => c.id === suggestForId)}
          onClose={() => setSuggestForId(null)}
          onApplied={() => { onRefresh?.(); }}
        />
      )}

      {briefingOpen && (
        <CharacterBriefingModal
          projectId={projectId}
          onClose={() => setBriefingOpen(false)}
          onAddAll={async (chars) => {
            // Añadir uno a uno usando el callback onAdd existente
            for (const c of chars) {
              // Limpiar campos vacíos para no enviarlos
              const payload = {};
              Object.entries(c).forEach(([k, v]) => {
                if (typeof v === "string" && v.trim()) payload[k] = v.trim();
              });
              if (payload.name) await onAdd(payload);
            }
          }}
        />
      )}

      {creating && (
        <div className="mb-3 rounded-lg border border-[#26775E]/40 bg-[#26775E]/5 p-3" data-testid="sp-character-new-form">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nombre del personaje"
            data-testid="sp-character-new-name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
              if (e.key === "Escape") setCreating(false);
            }}
            className="w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm outline-none focus:border-[#26775E]"
          />
          <label className="mt-2 block text-[10px] uppercase tracking-widest text-stone-500">Categoría</label>
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            data-testid="sp-character-new-category"
            className="mt-0.5 w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs outline-none focus:border-[#26775E]"
          >
            {CHARACTER_CATEGORIES.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.short}</option>
            ))}
          </select>
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => { setCreating(false); setNewName(""); setNewCategory("secundario"); }}
              className="rounded-full border border-stone-400 px-2 py-1 text-[10px] uppercase tracking-widest text-stone-700"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreate}
              data-testid="sp-character-new-save"
              className="rounded-full bg-[#26775E] px-2 py-1 text-[10px] uppercase tracking-widest text-white"
            >
              Crear
            </button>
          </div>
        </div>
      )}

      {(!characters || characters.length === 0) && !creating && (
        <p className="rounded-lg border border-dashed border-stone-300 p-4 text-center text-xs text-stone-500">
          Aún no hay personajes. La IA usará esta matriz como fuente de verdad al sugerir
          diálogos y escenas.
        </p>
      )}

      {/* Lista agrupada por categoría dramatúrgica. Cada grupo solo se renderiza
          si tiene al menos 1 personaje, para no llenar el panel de cabeceras
          vacías. */}
      {CHARACTER_CATEGORIES.map((cat) => {
        const list = grouped[cat.id] || [];
        if (list.length === 0) return null;
        return (
          <div key={cat.id} className="mb-3" data-testid={`sp-char-group-${cat.id}`}>
            <p className="mb-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#941620]" />
              {cat.label}
              <span className="text-stone-400">· {list.length}</span>
            </p>
            <ul className="space-y-2">
              {list.map((c) => (
                <li key={c.id} data-testid={`sp-character-${c.id}`} className="overflow-hidden rounded-lg border border-stone-200 bg-white">
                  <div
                    className="flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-stone-50"
                    onClick={() => setExpanded((id) => (id === c.id ? null : c.id))}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-sm font-bold uppercase tracking-wider text-stone-900">{c.name}</p>
                      <p className="truncate text-xs text-stone-500">
                        {[c.age, c.motivation].filter(Boolean).join(" · ") || "Sin descripción"}
                      </p>
                    </div>
                    <span className="text-stone-400">{expanded === c.id ? "−" : "+"}</span>
                  </div>
                  {expanded === c.id && (
                    <div className="border-t border-stone-200 bg-[#FAFAF6] px-3 py-3">
                      <input
                        value={c.name}
                        onChange={(e) => onUpdate(c.id, { name: e.target.value.toUpperCase() })}
                        data-testid={`sp-char-name-${c.id}`}
                        className="mb-2 w-full rounded border border-stone-300 bg-white px-2 py-1 font-mono text-sm font-bold uppercase outline-none focus:border-[#26775E]"
                      />
                      <label className="text-[10px] uppercase tracking-widest text-stone-500">Categoría</label>
                      <select
                        value={c.category || "secundario"}
                        onChange={(e) => onUpdate(c.id, { category: e.target.value })}
                        data-testid={`sp-char-category-${c.id}`}
                        className="mb-3 mt-0.5 w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs outline-none focus:border-[#26775E]"
                      >
                        {CHARACTER_CATEGORIES.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.short}</option>
                        ))}
                      </select>
                      {CHAR_FIELDS.map((field) => (
                        <div key={field.id} className="mb-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500">{field.label}</label>
                          <AutoTextarea
                            value={c[field.id] || ""}
                            onChange={(e) => onUpdate(c.id, { [field.id]: e.target.value })}
                            placeholder={field.placeholder}
                            data-testid={`sp-char-${field.id}-${c.id}`}
                            rows={field.id === "free" || field.id === "background" || field.id === "notes" ? 3 : 2}
                            className="mt-0.5 w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs outline-none focus:border-[#26775E]"
                          />
                        </div>
                      ))}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        {/* Botón "Sugerir contenido" SOLO si la ficha está poco
                            desarrollada (≥4 campos vacíos de 10). */}
                        {emptyFieldCount(c) >= 4 ? (
                          <button
                            onClick={() => setSuggestForId(c.id)}
                            data-testid={`sp-char-suggest-${c.id}`}
                            title="La IA lee el guion donde aparece este personaje y rellena los campos vacíos"
                            className="rounded-full border border-[#941620]/50 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#941620] transition hover:bg-[#941620] hover:text-white"
                          >
                            ✨ Sugerir contenido ({emptyFieldCount(c)} vacíos)
                          </button>
                        ) : <span />}
                        <button
                          onClick={() => {
                            if (confirm(`Eliminar el personaje "${c.name}"?`)) onDelete(c.id);
                          }}
                          data-testid={`sp-char-delete-${c.id}`}
                          className="rounded-full border border-red-400 px-3 py-1 text-[10px] uppercase tracking-widest text-red-600 hover:bg-red-50"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
